import { useState, useEffect } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useAnalyticsClient } from "@/analytics/wac-analytics";
import JiraSoftwareNav from "@/templates/software/jira/sections/nav-bar/software-jira-navbar";

import { Adg4SectionDivider } from "../../../components/adg4/section-divider/src";
import { ADG4CtaWithPolygonBackground } from "../../../patterns/adg4/cta-with-polygon-background/cta-with-polygon-background";
import { GlobalFooter } from "../../../patterns/global-footer/src/index";
import PaidSocialProof from "../../../patterns/paid-landings/paid-social-proof";
import SimpleImageTextSide from "../../../patterns/wpl/simple-image-text-side/src";
import { experiments, useExperimentExposure } from "../../../utils/experiment";
import { ParentScrollTracking } from "../../../utils/scroll-tracking";
import { PaidJiraAiBackground } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-background";
import { PaidJiraAiDisclaimer } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-disclaimer";
import { PaidJiraAiHeader } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-header";
import { PaidJiraAiPreviewModal } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-preview-modal";
import { PaidJiraAiTeamTypeMadLibPrompt } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-team-type-mad-lib-prompt";
import { PaidAlternatingImageText } from "../sections/paid-alternating-image-text";
import { PaidCtaSection } from "../sections/paid-cta-section";
import { PaidCustomSection } from "../sections/paid-custom-section";
import { PaidTabsSection } from "../sections/paid-tabs-section";

import {
    JiraAiFFLProvider,
    useJiraAiFFL
} from "./hooks/use-jira-ai-ffl-context";
import { HorizontalRule, PaidLandingProps } from "./paid-landing.template";

const promptContainerCss = css({
    marginBlockStart: "48px",
    padding: "0 60px",
    textAlign: "center"
});

export const PaidLandingJiraAiContent = ({
    landingType,
    showHorizontalRule,
    heroProps,
    bannerProps,
    tabsProps,
    customSection = undefined,
    alternatingTextAndImageProps,
    ctaProps,
    ctaWithPolyGonProps,
    testimonialProps,
    resultSectionDivider
}: PaidLandingProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();
    const { cgsDataLoading } = useJiraAiFFL();
    const [jiraFflExposureFired, fireJiraFflExposure] = useExperimentExposure(
        experiments.cfind4666JiraFfl
    );

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

    const showStaticTestimonials = !heroProps.animatedSocialBanner;

    useEffect(() => {
        if (!jiraFflExposureFired) {
            fireJiraFflExposure();
        }
    }, [jiraFflExposureFired, fireJiraFflExposure]);

    useEffect(() => {
        if (cgsDataLoading) {
            setIsPreviewModalOpen(true);
        }
    }, [cgsDataLoading]);

    useEffect(() => {
        if (!analyticsClient) {
            return; // be sure analyticsClient is available before firing event
        }
        createAnalyticsEvent({
            action: "viewed",
            eventType: "screen",
            name: "PaidLandingJiraAiTemplate",
            attributes: {
                flow: "paidLandingJiraAi"
            }
        }).fire();
    }, [analyticsClient, createAnalyticsEvent]);

    return (
        <main>
            <ParentScrollTracking
                pageId="paid-lp-jsw-ffl"
                scrollTrackingEnabled
            >
                <JiraSoftwareNav />
                <PaidJiraAiBackground />
                <PaidJiraAiHeader />
                <div css={promptContainerCss}>
                    <PaidJiraAiTeamTypeMadLibPrompt />
                </div>
                <PaidJiraAiDisclaimer />
                {isPreviewModalOpen && (
                    <PaidJiraAiPreviewModal
                        onClose={() => setIsPreviewModalOpen(false)}
                    />
                )}
                <PaidTabsSection {...tabsProps} />
                {customSection && (
                    <PaidCustomSection customSection={customSection} />
                )}
                {showHorizontalRule && <HorizontalRule />}
                {bannerProps && <SimpleImageTextSide {...bannerProps} />}
                {resultSectionDivider && (
                    <div
                        css={css({
                            marginBlockEnd: "-80px"
                        })}
                    >
                        <Adg4SectionDivider
                            bgColor={resultSectionDivider.color}
                            variation={resultSectionDivider.variation}
                        />
                    </div>
                )}
                <PaidAlternatingImageText
                    landingType={landingType}
                    alternatingImageTextProps={alternatingTextAndImageProps}
                />
                {ctaProps && (
                    <PaidCtaSection
                        ctaProps={ctaProps}
                        landingType={landingType}
                    />
                )}
                {ctaWithPolyGonProps && (
                    <>
                        <div
                            css={
                                resultSectionDivider &&
                                css({
                                    backgroundColor:
                                        resultSectionDivider?.color,
                                    marginBlockStart: "-80px",
                                    height: "200px"
                                })
                            }
                        />
                        <div
                            css={
                                resultSectionDivider &&
                                css({
                                    marginBlockStart: "-200px"
                                })
                            }
                        >
                            <ADG4CtaWithPolygonBackground
                                {...ctaWithPolyGonProps}
                            />
                        </div>
                    </>
                )}
                {showStaticTestimonials && testimonialProps && (
                    <PaidSocialProof {...testimonialProps} />
                )}
                <GlobalFooter />
            </ParentScrollTracking>
        </main>
    );
};

export const PaidLandingJiraAiTemplate = (
    props: PaidLandingProps & { anchor?: string; subAnchor?: string }
) => (
    <JiraAiFFLProvider anchor={props.anchor} subAnchor={props.subAnchor}>
        <PaidLandingJiraAiContent {...props} />
    </JiraAiFFLProvider>
);
